import { beTableAsComponent } from 'components/BeTable';
import { IBinLocation, IBinLocationStockExpectation, IItem } from 'interfaces'
import React, { FC, useCallback, useState } from 'react'
import Translation from 'utils/Language/Translation';
import Item from '../item';
import { useApi, useApiLoader } from 'utils/API';
import { distinct } from 'component_utils/utils';
import SimpleModal from 'components/SimpleModal';
import { Button, ButtonGroup, Container } from 'reactstrap';
import { FaPlus, FaTrash } from 'react-icons/fa';
import ProcessingButton from 'components/ProcessingButton';
import { FloatType, FormInput, FormObject, FormState, Labeled } from 'component_utils/FormUtils';
import { addNotification } from 'utils/NotificationManager';

interface Props {
  location: IBinLocation;
}

type IIBinLocationStockExpectation = IBinLocationStockExpectation & { item: IItem }

const Editor: FC<{ 
  binLocation: IBinLocation, 
  reload: any, 
  disableItemInput?: boolean,
  initialState: IBinLocationStockExpectation,
  trigger: React.ComponentProps<typeof SimpleModal>['trigger'];
}> = ({ binLocation, reload, initialState, disableItemInput, trigger }) => {
  const [state, setState] = useState<any>({})
  const api = useApi()

  return (
    <SimpleModal
      title=''
      trigger={trigger}
      onOpen={() => {
        setState(initialState);
      }}
      buttons={(close) => [
        <ProcessingButton
          color="primary"
          onClick={async () => {
            await api.binLocation.stockExpectations.saveStockExpectations(
              binLocation.code, state
            );
            addNotification('success', <Translation name="T.misc.success" />);
            reload();
            close();
          }}
          data-testid="btn-save"
        >
          <Translation name="T.misc.save" />
        </ProcessingButton>,
        <ProcessingButton color="secondary" onClick={close} data-testid="btn-close">
          <Translation name="T.misc.close" />
        </ProcessingButton>,
      ]}
    >
      <Container fluid>
        <FormState
          state={state}
          setState={setState}
          disableFormWrapper
        >
          <Labeled title={<Translation name="T.misc.item" />}>
            <FormObject<IItem>
              path="itemCode"
              disabled={disableItemInput}
              autocomplete={api.item.findAutocomplete}
              find={api.item.findItem}
              getCode={useCallback(it => it.code, [])}
              getEditValue={useCallback(it => it.visualCode, [])}
              render={useCallback((it) => <Item inline obj={it} />, [])}
            />
          </Labeled>

          <Labeled title={<Translation name="T.misc.minStock" />}>
            <FormInput
              path="minStock"
              typed={FloatType}
            />
          </Labeled>

          <Labeled title={<Translation name="T.misc.maxStock" />}>
            <FormInput
              path="maxStock"
              typed={FloatType}
            />
          </Labeled>
        </FormState>
      </Container>
    </SimpleModal>
  )
}

const Expectations = beTableAsComponent<IIBinLocationStockExpectation, { binLocation: IBinLocation, reload: any }>({
  tableId: 'binStockExpectations',
  columns: [
    {
      Header: <Translation name="T.misc.item" />,
      filterable: true,
      value: it => it.item.visualCode,
      Cell: ({ row }) => {
        return <Item inline obj={row.item} />;
      },
    },
    {
      Header: <Translation name="T.misc.minStock" />,
      hideBelow: 'sm',
      width: 20,
      className: 'text-right',
      value: (row) => row.minStock,
    },
    {
      Header: <Translation name="T.misc.maxStock" />,
      hideBelow: 'sm',
      width: 20,
      className: 'text-right',
      value: (row) => row.maxStock,
    },
    {
      Header: <Translation name="T.misc.edit" />,
      Footer: ({ binLocation, reload }) => (
        <Editor 
          binLocation={binLocation}
          reload={reload}
          initialState={{
            binCode: binLocation.code,
            itemCode: null,
            minStock: null,
            maxStock: null,

            extra: {},
            model: null
          }}
          trigger={open => (
            <Button color='primary' className='py-0' onClick={open}><FaPlus/></Button>
          )}
        />
      ),
      width: 10,
      className: 'text-center',
      Cell: ({ row, reload, binLocation }) => {
        const api = useApi()
        return (
          <Editor 
            binLocation={binLocation}
            reload={reload}
            initialState={row}
            disableItemInput
            trigger={open => (
              <ButtonGroup>
                <Button className="py-0" onClick={open} color="info">
                  <Translation name="T.misc.edit" />
                </Button>
                <ProcessingButton color='danger' className='py-0' onClick={async () => {
                  await api.binLocation.stockExpectations.deleteStockExpectations(binLocation.code, row.item.code)
                  reload()
                }}>
                  <FaTrash/>
                </ProcessingButton>
              </ButtonGroup>
            )}
          />
        )
      }
    }
  ],
});

export default ({ location }: Props) => {
  const binCode = location.code
  const { data, isLoading, reload } = useApiLoader(
    useCallback(async api => {
      const records = await api.binLocation.stockExpectations.getStockExpectations(binCode)
      const items = await api.item.getMultipleItems(distinct(records.map(it=>it.itemCode)))
      return records.map(it => ({
        ...it,
        item: items.find(i => i.code === it.itemCode)
      }))
    }, [binCode]),
    [],
    true
  )
  return (
    <Expectations
      data={data}
      loading={isLoading}
      getExtraProps={{ binLocation: location, reload }}
    />
  )
}